import type { createCollaboratorUserBody } from "@nlc/shared-types";

import { zodResolver } from "@hookform/resolvers/zod";
import { collaboratorSchema } from "@nlc/shared-types";
import { useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";

import { Button } from "@/components/button";

import { useCreateCollaborator } from "../hooks/useCreateUser";
import { AssignedBrands } from "./AssignedBrands";
import { AssignedRequests } from "./AssignedRequests";
import { BasicInformation } from "./BasicInformation";
import { CreatingUserOverlay } from "./CreatingUserOverlay";
import { SelectAccounts } from "./SelectAccounts";
import { SelectTimezone } from "./SelectTimezone";

export default function CreateCollaborator() {
  const { isPending, mutate } = useCreateCollaborator();
  const navigate = useNavigate();
  const {
    control,
    formState: { isValid },
    getValues,
    handleSubmit,
    reset,
  } = useForm<createCollaboratorUserBody>({
    defaultValues: {
      accountIds: [],
      assignedBrandIds: [],
      assignedRequestIds: [],
      email: "",
      name: "",
      timezoneId: 0,
    },
    mode: "onChange",
    resolver: zodResolver(collaboratorSchema),
  });

  const onSubmit = (data: createCollaboratorUserBody) => {
    mutate(data, {
      onSuccess: () => reset(),
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <CreatingUserOverlay isPending={isPending}>
        <BasicInformation
          control={control}
          emailField="email"
          nameField="name"
        />
        <SelectTimezone control={control} timezoneField="timezoneId" />
        <SelectAccounts accountIdsField="accountIds" control={control} />
        <div className="flex flex-col gap-y-lg mt-3 lg:grid lg:grid-cols-2 lg:gap-x-3xl w-full">
          <AssignedBrands
            control={control}
            name="assignedBrandIds"
            projectIds={getValues("accountIds")}
          />
          <AssignedRequests
            control={control}
            name="assignedRequestIds"
            projectIds={getValues("accountIds")}
          />
        </div>
        <div className="flex justify-end mt-5 pt-xl border-t border-border-secondary">
          <Button
            className="mr-2"
            onPress={() => navigate({ to: "/users" })}
            type="button"
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            isDisabled={!isValid || isPending}
            type="submit"
            variant="primary"
          >
            Create user
          </Button>
        </div>
      </CreatingUserOverlay>
    </form>
  );
}
